import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronDownIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { educationLevels } from '../data/degrees';
import { sanitizeTcpaHtml } from '../utils/sanitizeHtml';
import { TCPADisclosure } from './TCPADisclosure';
import { getProgramMatch, type ProgramTemplate } from '../data/programTemplates';
import { clicksSearch, leadsSearch, tcpaPost, tcpaSearch, type ContactProfile, type MMSchool, type SearchProfile, type TCPAResult } from '../services/mediaMatchers';
const startDateOptions = ['Immediately', '1–3 months', '3–6 months', '6–12 months', 'More than a year', 'Undecided'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  for (let y = currentYear + 2; y >= currentYear - 60; y--) years.push(String(y));
  years.push('Before 1965');
  return years;
}
const gradYearOptions = getGradYearOptions();
const STEP_LABELS = ['Program', 'Background', 'Location', 'Contact'];
interface HeroMatchFormProps {
  /** Programs offered in the Step 0 picker. */
  programs: ProgramTemplate[];
  /** Pre-selected program, when the page is about one. */
  initialProgramSlug?: string;
  /** Show each option's domain, for the all-programs index. */
  domainNameFor?: (program: ProgramTemplate) => string | undefined;
  heading?: string;
  subtext?: string;
}

/**
 * Lead form that lives directly in a program page hero, so a visitor can
 * complete the whole match without leaving the page they landed on.
 *
 * Step 0 picks the program, 1 collects background, 2 location, 3 contact. When
 * the page is about a single program, `initialProgramSlug` answers step 0 and
 * the picker is skipped entirely. Both Media Matchers Searches fire when step 2
 * completes — before the contact step renders — so the partner's own disclosure
 * is the text shown beside the consent checkbox.
 */
export function HeroMatchForm({
  programs,
  initialProgramSlug,
  domainNameFor,
  heading = 'Get Matched',
  subtext = 'Accredited programs. No obligation.'
}: HeroMatchFormProps) {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  // A page about one program has already answered the program question, so the
  // picker is skipped rather than asking the visitor to restate it.
  const hasFixedProgram = !!initialProgramSlug;
  const firstStep = hasFixedProgram ? 1 : 0;
  const [step, setStep] = useState(firstStep);
  const [programSlug, setProgramSlug] = useState(initialProgramSlug || '');
  const [programError, setProgramError] = useState('');
  const [zipLoading, setZipLoading] = useState(false);
  const [zipLookedUp, setZipLookedUp] = useState(false);
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep1,
    validateUniStep2,
    validateUniStep3
  } = useFormState();

  // Populates the LeadiD and TrustedForm hidden inputs before anything fires.
  useLeadTracking();
  const [leads, setLeads] = useState<MMSchool[] | null>(null);
  const [tcpa, setTcpa] = useState<TCPAResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tcpaFailed, setTcpaFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const searchFired = useRef(false);
  const selectedProgram = programs.find(p => p.slug === programSlug);
  const buildProfile = useCallback((): SearchProfile => {
    const context = selectedProgram ? getProgramMatch(selectedProgram) : {};
    return {
      degreeInterest: context.degreeInterest || '',
      areaStudy: context.areaStudy || '',
      mProgram: context.mProgram || '',
      currentEducation: formData.currentEducation,
      gradYear: formData.gradYear,
      startTimeframe: formData.startDate,
      militaryAffiliation: formData.isMilitary,
      zipCode: formData.zipCode.replace(/\D/g, ''),
      address: formData.address,
      city: formData.city,
      state: formData.state
    };
  }, [selectedProgram, formData]);
  const runSearches = useCallback(async () => {
    setSearchLoading(true);
    setTcpaFailed(false);
    const profile = buildProfile();
    const [leadsResult, tcpaResult] = await Promise.all([leadsSearch(profile), tcpaSearch(profile)]);
    setLeads(leadsResult || []);
    setTcpa(tcpaResult);
    if (!tcpaResult) setTcpaFailed(true);
    setSearchLoading(false);
  }, [buildProfile]);
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
        }
      }
    } catch {
      /* Fall through to the manual city/state fields. */
    } finally {
      setZipLookedUp(true);
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const handleProgramNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (!programSlug) {
      setProgramError('Please choose a program to continue.');
      return;
    }
    setStep(1);
  };
  const handleBackgroundNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (validateUniStep1()) setStep(2);
  };
  const handleLocationNext = (event: React.FormEvent) => {
    event.preventDefault();
    if (!validateUniStep2()) return;
    // Last input the payload needs, so the Searches run now and the partner's
    // disclosure is in hand by the time the consent checkbox renders.
    if (!searchFired.current) {
      searchFired.current = true;
      runSearches();
    }
    setStep(3);
  };
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitError('');
    if (!validateUniStep3()) return;
    if (!tcpa) {
      setTcpaFailed(true);
      setSubmitError('We could not load the consent details. Please retry before submitting.');
      return;
    }
    setIsSubmitting(true);
    const profile = buildProfile();
    const contact: ContactProfile = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: stripPhone(formData.phone)
    };
    await tcpaPost(tcpa.mRecordID, contact);
    const clicks = await clicksSearch(profile, contact, tcpa.mBrandID);
    setIsSubmitting(false);
    navigate('/matches', {
      state: {
        leads: leads || [],
        clicks: clicks || [],
        profile,
        contact,
        disclosure: tcpa.disclosure,
        excludeBrand: tcpa.mBrandID
      }
    });
  };
  const totalSteps = STEP_LABELS.length - firstStep;
  const currentStepNumber = step - firstStep + 1;
  const progressPercent = Math.round(currentStepNumber / totalSteps * 100);
  const selectClass = (hasError: boolean) => `block w-full pl-4 pr-10 py-3 text-sm rounded-lg border bg-white text-gray-900 appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-2 ${hasError ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:border-navy focus:ring-navy/20'}`;
  const inputClass = (hasError: boolean) => `block w-full px-4 py-3 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 ${hasError ? 'border-red-400 focus:ring-red-200' : 'border-gray-300 focus:border-navy focus:ring-navy/20'}`;
  const labelClass = 'block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider';
  const primaryButton = 'w-full bg-cta hover:bg-cta-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-3.5 px-6 rounded-lg text-sm tracking-wide uppercase transition-colors duration-200 inline-flex items-center justify-center gap-2';
  const backButton = 'inline-flex items-center justify-center px-4 py-3.5 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-60 transition-colors duration-200';
  const domains = domainNameFor ? Array.from(new Set(programs.map(p => domainNameFor(p) || '').filter(Boolean))) : [];
  const programOptions = domainNameFor ? domains.map(domainName => <optgroup key={domainName} label={domainName} data-id="element-435" data-mp="2co9266hxe5yn">
          {programs.filter(p => domainNameFor(p) === domainName).map(p => <option key={p.slug} value={p.slug} data-id="element-436" data-mp="358hasrktk3o3">
                {p.name} ({p.credential})
              </option>)}
        </optgroup>) : programs.map(p => <option key={p.slug} value={p.slug} data-id="element-437" data-mp="ort1k8t3fh74">
          {p.name} ({p.credential})
        </option>);
  const renderStep = () => {
    if (step === 0) {
      return <form onSubmit={handleProgramNext} className="space-y-5" data-id="element-438" data-mp="yi49s7jt8cjt">
          <div data-id="element-439" data-mp="yl06jsjx8v6o">
            <label htmlFor="hero-program" className={labelClass} data-id="element-440" data-mp="at1q6u5m89mo">
              Program of Interest
            </label>
            <div className="relative" data-id="element-441" data-mp="5urxqrb9zn4r">
              <select id="hero-program" value={programSlug} onChange={e => {
              setProgramSlug(e.target.value);
              if (e.target.value) setProgramError('');
            }} aria-invalid={!!programError} className={selectClass(!!programError)} data-id="element-442" data-mp="5vt96ww7ebn0">
                <option value="" data-id="element-443" data-mp="1r69voxmlo7l8">-- Choose a Program --</option>
                {programOptions}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-444" data-mp="1nfdz6h6uuxh3">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-445" data-mp="1jgevt10rv8rr" />
              </span>
            </div>
            {programError && <p className="mt-1.5 text-xs text-red-600" data-id="element-446" data-mp="19p0my2mxkwly">{programError}</p>}
          </div>
          <button type="submit" className={primaryButton} data-id="element-447" data-mp="14po3lrv9q7o4">
            Get Started
          </button>
        </form>;
    }
    if (step === 1) {
      return <form onSubmit={handleBackgroundNext} className="space-y-4" data-id="element-448" data-mp="vopxn419rq2w">
          <div data-id="element-449" data-mp="vozqczdp8bca">
            <label htmlFor="hero-education" className={labelClass} data-id="element-450" data-mp="vqkkinw3ps9m">
              Highest Level of Education
            </label>
            <div className="relative" data-id="element-451" data-mp="2q3aet6qdt3hj">
              <select id="hero-education" value={formData.currentEducation} onChange={e => updateFormData('currentEducation', e.target.value)} className={selectClass(!!errors.currentEducation)} data-id="element-452" data-mp="2q28q3zgg0ihc">
                <option value="" data-id="element-453" data-mp="2xkfc3uqu4wv8">-- Please Select --</option>
                {educationLevels.map(level => <option key={level} value={level} data-id="element-454" data-mp="32f2yno2a3h76">
                    {level}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-455" data-mp="29p49lso33k2i">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-456" data-mp="2eo72keqshzu4" />
              </span>
            </div>
            {errors.currentEducation && <p className="mt-1.5 text-xs text-red-600" data-id="element-457" data-mp="2nnu5j4z63dpa">
                {errors.currentEducation}
              </p>}
          </div>

          <div data-id="element-458" data-mp="3v1499b4ubaya">
            <label htmlFor="hero-gradyear" className={labelClass} data-id="element-459" data-mp="3v1dxoua8la1v">
              High School Graduation Year
            </label>
            <div className="relative" data-id="element-460" data-mp="1zyheu3l7h0rb">
              <select id="hero-gradyear" value={formData.gradYear} onChange={e => updateFormData('gradYear', e.target.value)} className={selectClass(!!errors.gradYear)} data-id="element-461" data-mp="1zxfwde1v1bzk">
                <option value="" data-id="element-462" data-mp="2402vm00v1z6s">-- Please Select --</option>
                {gradYearOptions.map(year => <option key={year} value={year} data-id="element-463" data-mp="1ga8jjkod8enl">
                    {year}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-464" data-mp="1cahmx0c5r4b5">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-465" data-mp="1q2eb4tapgu29" />
              </span>
            </div>
            {errors.gradYear && <p className="mt-1.5 text-xs text-red-600" data-id="element-466" data-mp="xjik65y079zh">{errors.gradYear}</p>}
          </div>

          <div data-id="element-467" data-mp="slpbn698a3w1">
            <label htmlFor="hero-start" className={labelClass} data-id="element-468" data-mp="sk5wxk3800wd">
              When Would You Like to Start?
            </label>
            <div className="relative" data-id="element-469" data-mp="3466bfm9cmwqb">
              <select id="hero-start" value={formData.startDate} onChange={e => updateFormData('startDate', e.target.value)} className={selectClass(!!errors.startDate)} data-id="element-470" data-mp="38zj17zprl9kn">
                <option value="" data-id="element-471" data-mp="306xigjm4v2vt">-- Please Select --</option>
                {startDateOptions.map(opt => <option key={opt} value={opt} data-id="element-472" data-mp="3nuffatk98r5n">
                    {opt}
                  </option>)}
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400" data-id="element-473" data-mp="3dyvlqdt5y4k2">
                <ChevronDownIcon className="w-4 h-4" aria-hidden="true" data-id="element-474" data-mp="3ivvr0mi6v3a4" />
              </span>
            </div>
            {errors.startDate && <p className="mt-1.5 text-xs text-red-600" data-id="element-475" data-mp="27i0cnfz3yyjy">{errors.startDate}</p>}
          </div>

          <div className="flex gap-3 pt-1" data-id="element-476" data-mp="2mrevn9p3q9u8">
            {!hasFixedProgram && <button type="button" onClick={() => setStep(0)} className={backButton} data-id="element-477" data-mp="2mthwypvpfpe9">
                <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-478" data-mp="2e0ekajsvvgko" />
                Back
              </button>}
            <button type="submit" className={primaryButton} style={{
            backgroundColor: "#E3924E"
          }} data-id="element-479" data-mp="28y5q5k49y8gz">
              Continue
            </button>
          </div>
        </form>;
    }
    if (step === 2) {
      return <form onSubmit={handleLocationNext} className="space-y-4" data-id="element-480" data-mp="2wpumrco4pj3q">
          <div data-id="element-481" data-mp="2wo03s3wf412a">
            <label htmlFor="hero-zip" className={labelClass} data-id="element-482" data-mp="2wrny359osri2">
              ZIP Code
            </label>
            <input id="hero-zip" type="text" inputMode="numeric" maxLength={5} value={formData.zipCode} onChange={e => {
            const val = e.target.value.replace(/\D/g, '').slice(0, 5);
            updateFormData('zipCode', val);
            setZipLookedUp(false);
            if (val.length === 5) lookupZip(val);
          }} placeholder="12345" className={inputClass(!!errors.zipCode)} data-id="element-483" data-mp="1lao5s9ncaco1" />
            {errors.zipCode && <p className="mt-1.5 text-xs text-red-600" data-id="element-484" data-mp="1kxve98b4dwhh">{errors.zipCode}</p>}
            {zipLoading && <p className="mt-2 flex items-center text-xs text-gray-500" data-id="element-485" data-mp="1p0i6397sjheo">
                <LoaderIcon className="w-3 h-3 mr-1.5 animate-spin" aria-hidden="true" data-id="element-486" data-mp="1tve2smnz2sy4" />
                Looking up your location...
              </p>}
          </div>

          {zipLookedUp && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-487" data-mp="4jlyv3ouvi2s">
                <div data-id="element-488" data-mp="9h3z85ot4oz6">
                  <label htmlFor="hero-city" className={labelClass} data-id="element-489" data-mp="9glvknztf8h8">
                    City
                  </label>
                  <input id="hero-city" type="text" value={formData.city} onChange={e => updateFormData('city', e.target.value)} placeholder="City" className={inputClass(false)} data-id="element-490" data-mp="1hh8aapaabmd" />
                </div>
                <div data-id="element-491" data-mp="6i56phfuduqm">
                  <label htmlFor="hero-state" className={labelClass} data-id="element-492" data-mp="kf1vzegbb45e">
                    State
                  </label>
                  <input id="hero-state" type="text" value={formData.state} onChange={e => updateFormData('state', e.target.value)} placeholder="State" className={inputClass(false)} data-id="element-493" data-mp="fffkpak1nxz8" />
                </div>
              </div>
              <div data-id="element-494" data-mp="z4g46j85jfj7">
                <label htmlFor="hero-address" className={labelClass} data-id="element-495" data-mp="z594p2gip8sw">
                  Street Address{' '}
                  <span className="font-normal normal-case text-gray-400" data-id="element-496" data-mp="1httf90ii5dq9">
                    (optional)
                  </span>
                </label>
                <input id="hero-address" type="text" value={formData.address} onChange={e => updateFormData('address', e.target.value)} placeholder="123 Main St" className={inputClass(false)} data-id="element-497" data-mp="1mph39cl900u6" />
              </div>
            </>}

          <div className="flex gap-3 pt-1" data-id="element-498" data-mp="13ujzg1y7klmt">
            <button type="button" onClick={() => setStep(1)} className={backButton} data-id="element-499" data-mp="q1kh7s71kdbk">
              <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-500" data-mp="u3z36l60bt72" />
              Back
            </button>
            <button type="submit" className={primaryButton} data-id="element-501" data-mp="g3wof7szxfw1">
              Continue
            </button>
          </div>
        </form>;
    }
    return <form onSubmit={handleSubmit} className="space-y-4" data-id="element-502" data-mp="10l0rznxutrjj">
        <input id="leadid_token" name="leadid_token" type="hidden" data-id="element-503" data-mp="10iektpevle1u" />
        <input id="xxTrustedFormCertUrl" name="xxTrustedFormCertUrl" type="hidden" data-id="element-504" data-mp="vmphrc3ndf0s" />

        <div className="grid grid-cols-2 gap-3" data-id="element-505" data-mp="rlx9om6ihg63">
          <div data-id="element-506" data-mp="mlizaxhftu4w">
            <label htmlFor="hero-first" className={labelClass} data-id="element-507" data-mp="mjy558z1cd7k">
              First Name
            </label>
            <input id="hero-first" type="text" autoComplete="given-name" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} placeholder="Jane" className={inputClass(!!errors.firstName)} data-id="element-508" data-mp="hn7rstem7vpu" />
            {errors.firstName && <p className="mt-1.5 text-xs text-red-600" data-id="element-509" data-mp="3v0t0829lctb3">{errors.firstName}</p>}
          </div>
          <div data-id="element-510" data-mp="3q2hr5tr4emtx">
            <label htmlFor="hero-last" className={labelClass} data-id="element-511" data-mp="3q1yjr6z3jgu5">
              Last Name
            </label>
            <input id="hero-last" type="text" autoComplete="family-name" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} placeholder="Doe" className={inputClass(!!errors.lastName)} data-id="element-512" data-mp="25j5ulq14zy7c" />
            {errors.lastName && <p className="mt-1.5 text-xs text-red-600" data-id="element-513" data-mp="1mtz8dt14wogs">{errors.lastName}</p>}
          </div>
        </div>

        <div data-id="element-514" data-mp="1rsivhwrgs6aq">
          <label htmlFor="hero-email" className={labelClass} data-id="element-515" data-mp="1rs93y4qrtr2x">
            Email Address
          </label>
          <input id="hero-email" type="email" autoComplete="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} placeholder="jane@example.com" className={inputClass(!!errors.email)} data-id="element-516" data-mp="1vrhyvcxjrv02" />
          {errors.email && <p className="mt-1.5 text-xs text-red-600" data-id="element-517" data-mp="fjdbh0wjarin">{errors.email}</p>}
        </div>

        <div data-id="element-518" data-mp="kh5b6h3f7hxe">
          <label htmlFor="hero-phone" className={labelClass} data-id="element-519" data-mp="kglwraczbbo4">
            Phone Number
          </label>
          <input id="hero-phone" type="tel" autoComplete="tel" maxLength={13} value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} placeholder="(555)123-4567" className={inputClass(!!errors.phone)} data-id="element-520" data-mp="okjv3iuhcig4" />
          {errors.phone && <p className="mt-1.5 text-xs text-red-600" data-id="element-521" data-mp="5rz7aftn13j6">{errors.phone}</p>}
        </div>

        {searchLoading ? <p className="flex items-center gap-2 text-xs text-gray-500 pt-1" data-id="element-522" data-mp="alk80fmm8bm2">
            <LoaderIcon className="w-3.5 h-3.5 animate-spin" aria-hidden="true" data-id="element-523" data-mp="398y47qi6gmf2" />
            Loading consent details...
          </p> : tcpaFailed ? <div className="rounded-lg border border-red-200 bg-red-50 p-3" data-id="element-524" data-mp="36pkzzzu3ul75">
            <p className="text-xs font-medium text-red-800" data-id="element-525" data-mp="31pyn5ri6v6yv">
              We couldn't load the consent details.
            </p>
            <button type="button" onClick={() => {
          searchFired.current = true;
          runSearches();
        }} className="mt-2 inline-flex items-center rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100 transition-colors duration-200" data-id="element-526" data-mp="2wq3xtse2apfx">
              Retry
            </button>
          </div> : <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-0.5 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0 accent-[#5B4EA8]" data-id="element-527" data-mp="2e0nhpwpw8oee">
            {tcpa?.disclosure ? <span dangerouslySetInnerHTML={{
          __html: sanitizeTcpaHtml(tcpa.disclosure)
        }} data-id="element-528" data-mp="1qch76qx3czq6" /> : 'By submitting this form, you are giving your express written consent to be contacted regarding educational opportunities by email, telephone, or text, including through the use of automated technology, at the number provided above. Consent is not a condition of enrollment.'}
          </TCPADisclosure>}

        {submitError && <p className="text-xs text-red-600" data-id="element-529" data-mp="2e19yub6oklez">{submitError}</p>}

        <div className="flex gap-3 pt-1" data-id="element-530" data-mp="1ze6y7vn29wnf">
          <button type="button" onClick={() => setStep(2)} disabled={isSubmitting} className={backButton} data-id="element-531" data-mp="1zd5ktkikoy2j">
            <ChevronLeftIcon className="w-4 h-4 mr-1" aria-hidden="true" data-id="element-532" data-mp="2s22qmo5ll2ee" />
            Back
          </button>
          <button type="submit" disabled={isSubmitting} className={primaryButton} data-id="element-533" data-mp="2wu4aow90bxez">
            {isSubmitting && <LoaderIcon className="w-4 h-4 animate-spin" aria-hidden="true" data-id="element-534" data-mp="gf9138th8uwi" />}
            {isSubmitting ? 'Submitting...' : 'See My Matches'}
          </button>
        </div>
      </form>;
  };
  return <div className="bg-white rounded-2xl shadow-xl overflow-hidden ring-1 ring-gray-900/5" data-id="element-535" data-mp="q4k6olh5nhfn">
      <div className="bg-navy px-6 py-4" style={{
      backgroundColor: "#5FA396"
    }} data-id="element-536" data-mp="2epwkbo806z3">
        <h2 className="text-white font-bold text-center text-sm tracking-widest uppercase" data-id="element-537" data-mp="3tliyxiur2c57">
          {heading}
        </h2>
        <p className="text-white/70 text-xs text-center mt-1" data-id="element-538" data-mp="bg666h77o529">{subtext}</p>
      </div>

      <div className="px-6 pt-5" data-id="element-539" data-mp="6gbhmkawrrkd">
        <div className="flex items-center justify-between mb-1.5" data-id="element-540" data-mp="6g1leez9so68">
          <span className="text-xs font-bold uppercase tracking-wider text-navy" data-id="element-541" data-mp="1mujo75fa9qad">
            Step {currentStepNumber} of {totalSteps} · {STEP_LABELS[step]}
          </span>
          <span className="text-xs text-gray-500" data-id="element-542" data-mp="2d66dcq3zraax">{progressPercent}%</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden" role="progressbar" aria-valuenow={progressPercent} aria-valuemin={0} aria-valuemax={100} aria-label="Form progress" data-id="element-543" data-mp="2d8j33pfzqoyj">
          <motion.div className="h-full rounded-full bg-crimson" initial={false} animate={{
          width: `${progressPercent}%`
        }} transition={{
          duration: 0.25,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-544" data-mp="2s477belb7c2f" />
        </div>
      </div>

      <div className="p-6" data-id="element-545" data-mp="35uamjllru5id">
        <AnimatePresence mode="wait" initial={false} data-id="element-546" data-mp="35w554t9kllja">
          <motion.div key={step} initial={{
          opacity: 0,
          y: reduceMotion ? 0 : 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: reduceMotion ? 0 : -8
        }} transition={{
          duration: 0.18,
          ease: [0.23, 1, 0.32, 1]
        }} data-id="element-547" data-mp="3atnj4y2g2qy5">
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}